const content = {
  en: {
    meta: {
      title: 'Dvir Magen — Full Stack Developer',
      description:
        'Dvir Magen — Full Stack Developer building AI-powered, cloud-native web applications with React, Next.js, TypeScript and AWS.',
    },
    nav: {
      about: 'About',
      experience: 'Experience',
      education: 'Education',
      projects: 'Projects',
      skills: 'Skills',
      connect: 'Connect',
      resume: 'Resume',
      langToggle: 'עב',
      langToggleLabel: 'Switch to Hebrew',
      themeToggleLabelDark: 'Switch to light mode',
      themeToggleLabelLight: 'Switch to dark mode',
    },
    hero: {
      name: 'Dvir Magen',
      badge: 'OPEN TO WORK',
      subtitleLines: [
        'FULL-STACK DEVELOPER PASSIONATE ABOUT DEVELOPING BY AI TOOLS,',
        'CREATING CLEAN CODE APPS, AND THOUGHTFUL USER EXPERIENCES.',
        'I ENJOY SOLVING REAL-WORLD PROBLEMS,',
        'EXPLORING NEW TECHNOLOGIES,',
        'AND TAKING ON MEANINGFUL TECHNICAL CHALLENGES.',
      ],
      tags: ['React', 'Next.js', 'TypeScript', 'AWS', 'RAG / LLM apps'],
      ctaPrimary: 'Get in touch',
      ctaSecondary: 'View projects',
      downloadCv: 'Download CV',
      copied: 'Copied!',
    },
    about: {
      sectionTitle: 'About',
      eyebrow: 'WHO I AM',
      paragraphs: [
        "I'm a Full Stack Developer with a B.Sc. in Computer Science from the Academic College of Tel Aviv–Yafo, focused on shipping fast, reliable products inside fast-moving, cloud-first teams.",
        'I recently wrapped up my role at <slashin>Slash-in</slashin>, where I built scalable, cloud-native web applications end-to-end — from intuitive React interfaces to secure, serverless backends on AWS Amplify. I\'m now actively looking for my next full-stack opportunity.',
        'My day-to-day spans React, Next.js, and TypeScript on the frontend, and serverless architecture, authentication, and data modeling on the backend — always with an eye on performance, security, and maintainability.',
        'I lean on modern, AI-assisted workflows (Cursor, Claude) to move faster without cutting corners, and I care about clean, modular, well-tested code that scales with the product.',
        "I'm a motivated, detail-driven team player who enjoys solving hard problems and shipping things that matter.",
      ],
    },
    education: {
      sectionTitle: 'Education',
      items: {
        'tau-college': {
          title: 'B.Sc. Computer Science',
          academy: 'The Academic College of Tel Aviv-Yafo',
        },
      },
    },
    experience: {
      sectionTitle: 'Experience',
      stackLabel: 'Tech Stack:',
      visitPrefix: 'Visit',
      items: {
        'slash-in': {
          title: 'Full Stack Developer',
          organization: 'Slash-in',
          achievements: [
            'Design, develop, and maintain scalable, cloud-based web applications covering the full development cycle — from intuitive user interfaces to secure, serverless backend services powered by AWS Amplify',
            'Build rich, responsive interfaces using React, Next.js, and TypeScript, combined with MUI and react-hook-form-mui',
            'Build modular and reusable UI components, multi-step forms, and dynamic editor flows that enhance user experience and keep the codebase consistent',
            'Use AWS Amplify to build and manage serverless cloud infrastructure, handling authentication, authorization, and data storage',
            'Design secure data models, manage user permissions, and connect Amplify services with the React frontend',
          ],
        },
      },
    },
    projects: {
      sectionTitle: 'Projects',
      keyAchievements: 'Key Achievements:',
      techStack: 'Tech Stack:',
      viewGithub: 'View on GitHub →',
      back: 'Back',
      notFound: 'Project not found.',
      backToPortfolio: 'Back to portfolio',
      labels: {
        rag: 'RAG Agent',
        cr: 'Cosmic Race',
        abs: 'Alternative Banking System',
        nlp: 'NLP — Unrecognized Requests',
        weetbis: 'Weet&Bis',
      },
      items: {
        rag: {
          title: 'RAG Agent',
          description:
            'A full SaaS platform where every user builds their own AI agent — answering questions exclusively from their private uploaded knowledge base. No hallucinations, no generic answers: only what lives inside your documents.',
          achievements: [
            'Built end-to-end RAG pipeline: documents are chunked (~500 tokens with 50-token overlap), embedded via OpenAI text-embedding-3-small (1536 dims), and stored in Supabase Postgres using pgvector with an IVFFlat index for fast approximate nearest-neighbor search',
            'Implemented contextual retrieval by concatenating the last 4 conversation messages into the embedding query — making follow-up questions like "tell me more about that" work correctly',
            'Enforced complete multi-tenant data isolation using Postgres Row Level Security (RLS) at the database engine level — not application-layer validation',
            'Built native streaming with the Web ReadableStream API and OpenAI SDK directly, bypassing framework abstractions for full control over token-by-token response delivery',
            'Designed a per-user admin panel with Upload, Documents, and Settings tabs — including configurable persona name, system prompt, model, temperature, and retrieval k',
            'Implemented batch ingestion (20 chunks per insert) to stay within the 30-second serverless function timeout on large documents',
            'Built a golden-question evaluation harness to verify answer correctness by keyword scoring against predefined ground-truth questions',
          ],
        },
        cr: {
          title: 'Cosmic Race',
          description:
            'A 2D multiplayer racing game where players race across the universe — competing in online tournaments against players worldwide, or gathering with friends in FriendMode using their personal phones as wireless remote controllers.',
          achievements: [
            'Designed two core game modes: online multiplayer with global leaderboards, and FriendMode where friends join a single host computer and control their characters using their personal phones as remote controllers displayed on a TV or monitor',
            'Built a dedicated Java game server hosted on Azure, handling real-time socket communication, timeouts, lag compensation, and connection reliability between clients',
            'Developed the game client in Unity (C#) with 2D pixel-art graphics and cross-platform support',
            'Implemented HTML5 mobile controls so players can use their personal phones as wireless gamepads — no installation required',
            'Designed a coin-collection system along race tracks, letting players upgrade their characters through the official game website',
            'Created a Node.js web server managing user accounts, character progression, and persistent game data',
          ],
        },
        abs: {
          title: 'Alternative Banking System',
          description:
            'A client/server banking application system for lenders and borrowers with separate admin and client interfaces.',
          achievements: [
            'Developed JavaFX applications for both admin and client interfaces',
            'Implemented HTTP-based communication between clients and server',
            'Designed and implemented a multi-threading architecture including thread pool management with internal blocking queues',
            'Ensured thread synchronization for concurrent operations',
          ],
        },
        nlp: {
          title: 'Analysis of Unrecognized User Requests',
          description:
            'A Python NLP pipeline for analyzing unrecognized user requests in goal-oriented dialog systems (virtual assistants). Automatically clusters unhandled requests, extracts diverse representatives per cluster, and auto-labels them — replacing manual inspection of tens of thousands of daily requests.',
          achievements: [
            'Applied intent-classifier confidence thresholding to detect unrecognized requests and route them into an unhandled pool for automated analysis',
            'Developed a centroid-based clustering algorithm using sentence embeddings — auto-discovers the number of clusters (no predefined K) and tolerates outliers rather than forcing all requests into clusters',
            'Built a diverse cluster-representative extraction system selecting K representatives per cluster that mirror the different ways users express the same underlying need',
            'Implemented automated cluster naming using high-frequency, well-formed word n-grams scored by POS-sequence fluency and within-cluster frequency (faithfulness)',
            'Evaluated clustering quality against ground-truth datasets using Rand Index (RI) and Adjusted Rand Index (ARI) metrics',
          ],
        },
        weetbis: {
          title: 'Weet&Bis — Restaurant Management System',
          description:
            'A backend service for a restaurant management system handling restaurants, ratings, dishes, and orders through RESTful APIs.',
          achievements: [
            'Developed RESTful APIs for restaurant, rating, dish, and order management',
            'Implemented a PostgreSQL database for scalable data storage',
            'Built the application using the Java Spring Boot framework',
            'Designed a stateless API architecture following REST principles for efficient client-server communication',
          ],
        },
      },
    },
    skills: {
      sectionTitle: 'Skills',
      groups: {
        languages: 'Languages',
        frontend: 'Frontend',
        backendCloud: 'Backend & Cloud',
        aiCore: 'AI & Core Concepts',
      },
    },
    footer: {
      connectTitle: 'Connect',
      sendMessage: 'Send a direct message',
      copied: 'Copied!',
      rights: '© {year} Dvir Magen. All rights reserved.',
    },
    codeFeature: {
      typeLabel: 'boolean',
      varLabel: 'lookingForProgrammer',
      sendBtn: 'send me message',
    },
    modal: {
      title: 'Send a message',
      nameLabel: 'Your name',
      namePlaceholder: 'Jane Doe',
      messageLabel: 'Message',
      messagePlaceholder: "What's on your mind?",
      nameRequired: 'Please enter your name.',
      messageRequired: 'Please enter a message.',
      genericError: 'Please fix the errors above.',
      sendFailed: 'Failed to send. Please try again.',
      sending: 'Sending…',
      send: 'Send',
      cancel: 'Cancel',
      close: 'Close',
      thankYou: 'Thank you!',
      successMessage: "Your message is in. I'll get back to you soon.",
      successMessageAlt: 'Your message has been sent successfully!',
      gotIt: 'Got it',
    },
  },
  he: {
    meta: {
      title: 'דביר מגן — מפתח Full Stack',
      description:
        'דביר מגן — מפתח Full Stack הבונה אפליקציות ווב סקיילביליות, ענניות ומבוססות כלי AI עם React, Next.js, TypeScript ו-AWS.',
    },
    nav: {
      about: 'אודות',
      experience: 'ניסיון',
      education: 'השכלה',
      projects: 'פרויקטים',
      skills: 'כישורים',
      connect: 'צור קשר',
      resume: 'קורות חיים',
      langToggle: 'EN',
      langToggleLabel: 'החלף לאנגלית',
      themeToggleLabelDark: 'החלף למצב בהיר',
      themeToggleLabelLight: 'החלף למצב כהה',
    },
    hero: {
      name: 'דביר מגן',
      badge: 'פנוי לעבודה',
      subtitleLines: [
        'מפתח Full Stack,',
        'נהנה לפתור בעיות אמיתיות,',
        'לחקור טכנולוגיות חדשות ואתגרים טכניים משמעותיים.',
      ],
      tags: ['React', 'Next.js', 'TypeScript', 'AWS', 'RAG / LLM'],
      ctaPrimary: 'יצירת קשר',
      ctaSecondary: 'צפייה בפרויקטים',
      downloadCv: 'הורדת קורות חיים',
      copied: 'הועתק!',
    },
    about: {
      sectionTitle: 'אודות',
      eyebrow: 'מי אני',
      paragraphs: [
        'אני מפתח Full Stack בעל תואר B.Sc. במדעי המחשב מהמכללה האקדמית תל אביב–יפו, שמתמקד באספקת מוצרים מהירים ואמינים בתוך צוותי הייטק דינמיים ומבוססי ענן.',
        'לאחרונה סיימתי את תפקידי ב-<slashin>Slash-in</slashin>, שם בניתי אפליקציות ווב סקיילביליות ומבוססות ענן מקצה לקצה — מממשקי React אינטואיטיביים ועד שירותי Backend מאובטחים וללא שרת (Serverless) על גבי AWS Amplify. כיום אני מחפש באופן פעיל את ההזדמנות הבאה שלי בתחום Full Stack.',
        'העבודה השוטפת שלי משלבת React, Next.js ו-TypeScript בצד הלקוח, לצד ארכיטקטורת Serverless, אימות משתמשים ומידול נתונים בצד השרת — תמיד תוך דגש על ביצועים, אבטחה ותחזוקתיות.',
        'אני נעזר בכלי AI מודרניים (Cursor, Claude) כדי להתקדם מהר יותר בלי לוותר על איכות, ומקפיד על קוד נקי, מודולרי ונבדק היטב שגדל יחד עם המוצר.',
        'אני חבר צוות בעל מוטיבציה גבוהה ומוקפד על פרטים, שאוהב לפתור בעיות מורכבות ולהוציא לעולם דברים שבאמת משנים.',
      ],
    },
    education: {
      sectionTitle: 'השכלה',
      items: {
        'tau-college': {
          title: 'תואר ראשון (B.Sc.) במדעי המחשב',
          academy: 'המכללה האקדמית תל אביב-יפו',
        },
      },
    },
    experience: {
      sectionTitle: 'ניסיון תעסוקתי',
      stackLabel: 'טכנולוגיות:',
      visitPrefix: 'בקרו באתר',
      items: {
        'slash-in': {
          title: 'מפתח Full Stack',
          organization: 'Slash-in',
          achievements: [
            'תכנון, פיתוח ותחזוקה של אפליקציות ווב סקיילביליות מבוססות ענן, לאורך כל מחזור הפיתוח — מממשקי משתמש אינטואיטיביים ועד שירותי Backend מאובטחים וללא שרת המבוססים על AWS Amplify',
            'בניית ממשקים עשירים ורספונסיביים באמצעות React, Next.js ו-TypeScript, בשילוב MUI ו-react-hook-form-mui',
            'בניית רכיבי ממשק מודולריים וניתנים לשימוש חוזר, טפסים רב-שלביים וזרימות עריכה דינמיות המשפרות את חוויית המשתמש ושומרות על עקביות הקוד',
            'שימוש ב-AWS Amplify לבניה וניהול של תשתית ענן ללא שרת, כולל אימות, הרשאות ואחסון נתונים',
            'תכנון מודלים בטוחים לנתונים, ניהול הרשאות משתמשים וחיבור שירותי Amplify לצד ה-React',
          ],
        },
      },
    },
    projects: {
      sectionTitle: 'פרויקטים',
      keyAchievements: 'עיקרי ההישגים:',
      techStack: 'טכנולוגיות:',
      viewGithub: 'צפייה ב-GitHub ←',
      back: 'חזרה',
      notFound: 'הפרויקט לא נמצא.',
      backToPortfolio: 'חזרה לתיק העבודות',
      labels: {
        rag: 'RAG Agent',
        cr: 'Cosmic Race',
        abs: 'מערכת בנקאות חלופית',
        nlp: 'NLP — פניות לא מזוהות',
        weetbis: 'Weet&Bis',
      },
      items: {
        rag: {
          title: 'RAG Agent',
          description:
            'פלטפורמת SaaS מלאה שבה כל משתמש בונה סוכן AI משלו — שעונה שאלות אך ורק על בסיס בסיס הידע הפרטי שהעלה. ללא הזיות, ללא תשובות גנריות: רק מה שנמצא במסמכים שלך.',
          achievements: [
            'בניית pipeline RAG מקצה לקצה: מסמכים מחולקים לקטעים (~500 tokens עם חפיפה של 50 tokens), מוטמעים דרך OpenAI text-embedding-3-small (1536 ממדים), ונשמרים ב-Supabase Postgres עם pgvector ואינדקס IVFFlat לחיפוש מהיר',
            'מימוש Contextual Retrieval על ידי שרשור 4 הודעות שיחה אחרונות לשאילתת ההטמעה — כדי שאלות המשך כמו "ספר לי עוד" יעבדו נכון',
            'אכיפת בידוד נתונים מלא בין משתמשים באמצעות Row Level Security (RLS) של Postgres ברמת מנוע הנתונים — לא ברמת הקוד',
            'בניית Streaming נייטיב עם Web ReadableStream API ו-OpenAI SDK ישירות, ללא תלות בספריות ביניים, לשליטה מלאה על הזרמת התשובות',
            'תכנון פנל ניהול per-user עם טאבים: Upload, Documents, Settings — כולל הגדרת שם Persona, System Prompt, מודל, טמפרטורה ו-k לאחזור',
            'מימוש Batch Ingestion (20 chunks לכל הכנסה) כדי לא לחרוג מ-timeout של 30 שניות ב-Serverless Function',
            'בניית Evaluation Harness לאימות נכונות תשובות באמצעות ניקוד מילות מפתח מול שאלות זהב מוגדרות מראש',
          ],
        },
        cr: {
          title: 'Cosmic Race',
          description:
            'משחק מרוץ דו-ממדי מרובה משתתפים, שבו שחקנים מתחרים ברחבי היקום — בתחרויות מקוונות מול שחקנים מרחבי העולם, או מתאספים עם חברים ב-FriendMode תוך שימוש בפלאפון האישי כשלט רחוק אלחוטי.',
          achievements: [
            'תכנון שני מצבי משחק עיקריים: תחרויות מקוונות עם טבלאות דירוג גלובליות, ו-FriendMode שבו חברים מצטרפים ממחשב מארח אחד ושולטים בדמות שלהם באמצעות הפלאפון האישי — המוצגת על מסך טלוויזיה או מחשב',
            'פיתוח שרת משחק ייעודי ב-Java המתארח ב-Azure, המטפל בתקשורת Sockets בזמן אמת, טיימאאוטים, פיצוי לאגים ואמינות חיבור בין לקוחות',
            'בניית לקוח המשחק ב-Unity (C#) עם גרפיקת פיקסל-ארט דו-ממדית ותמיכה רב-פלטפורמית',
            'מימוש ממשקי שליטה מבוססי HTML5 לנייד, המאפשרים לשחקנים להשתמש בפלאפון האישי כגיימפד אלחוטי — ללא צורך בהתקנה',
            'עיצוב מערכת איסוף מטבעות לאורך מסלולי המרוץ, המאפשרת לשחקנים לשדרג את דמויותיהם דרך האתר הרשמי של המשחק',
            'בניית שרת ווב ב-Node.js לניהול חשבונות משתמשים, התקדמות דמויות ושמירת נתוני משחק',
          ],
        },
        abs: {
          title: 'מערכת בנקאות חלופית',
          description:
            'מערכת בנקאות מסוג לקוח/שרת עבור מלווים ולווים, עם ממשקי ניהול ולקוח נפרדים.',
          achievements: [
            'פיתוח אפליקציות JavaFX עבור ממשק הניהול וממשק הלקוח',
            'מימוש תקשורת מבוססת HTTP בין הלקוחות לשרת',
            'תכנון ומימוש ארכיטקטורת Multi-threading הכוללת ניהול מאגר Threads עם תורי חסימה פנימיים',
            'הבטחת סנכרון Threads עבור פעולות מקבילות',
          ],
        },
        nlp: {
          title: 'ניתוח פניות משתמשים לא מזוהות',
          description:
            'מערכת NLP בפייתון לניתוח פניות משתמשים לא מזוהות במערכות דיאלוג ממוקדות-מטרה (עוזרים וירטואליים). מבצעת אשכול אוטומטי של פניות לא מטופלות, חילוץ נציגים מגוונים לכל אשכול ותיוג אוטומטי — ומחליפה בדיקה ידנית של עשרות אלפי פניות ביום.',
          achievements: [
            'החלת סף ביטחון (Confidence Thresholding) על מסווג כוונות לזיהוי פניות לא מזוהות והפנייתן לאגרגציה אוטומטית',
            'פיתוח אלגוריתם אשכול מבוסס-ציר (Centroid-Based) עם ייצוגי Embeddings — מגלה את מספר האשכולות אוטומטית ללא K קבוע מראש, ומאפשר פניות חריגות (Outliers) במקום לכפות שיוך לכל הנתונים',
            'בניית מערכת חילוץ נציגים מגוונים לכל אשכול — בוחרת K נציגים המשקפים את הדרכים השונות שבהן משתמשים מבטאים את אותה בקשה',
            'מימוש תיוג אוטומטי של אשכולות באמצעות n-גרמים בעלי תדירות גבוהה בתוך האשכול ורצף תגיות-חלק-דיבר (POS) תקין מבחינה לשונית',
            'הערכת איכות האשכול מול מערכי נתוני ייחוס בעזרת מדדי Rand Index (RI) ו-Adjusted Rand Index (ARI)',
          ],
        },
        weetbis: {
          title: 'Weet&Bis — מערכת ניהול מסעדות',
          description:
            'שירות Backend למערכת ניהול מסעדות, המטפל במסעדות, דירוגים, מנות והזמנות באמצעות RESTful APIs.',
          achievements: [
            'פיתוח RESTful APIs לניהול מסעדות, דירוגים, מנות והזמנות',
            'שילוב מסד נתונים PostgreSQL לאחסון נתונים סקיילבילי',
            'בניית האפליקציה באמצעות פריימוורק Java Spring Boot',
            'תכנון ארכיטקטורת API חסרת מצב (Stateless) בהתאם לעקרונות REST לתקשורת יעילה בין לקוח לשרת',
          ],
        },
      },
    },
    skills: {
      sectionTitle: 'כישורים',
      groups: {
        languages: 'שפות תכנות',
        frontend: 'פרונטאנד',
        backendCloud: 'בקאנד וענן',
        aiCore: 'AI ומושגי ליבה',
      },
    },
    footer: {
      connectTitle: 'צור קשר',
      sendMessage: 'שליחת הודעה ישירה',
      copied: 'הועתק!',
      rights: '© {year} דביר מגן. כל הזכויות שמורות.',
    },
    codeFeature: {
      typeLabel: 'boolean',
      varLabel: 'lookingForProgrammer',
      sendBtn: 'שלחו לי הודעה',
    },
    modal: {
      title: 'שליחת הודעה',
      nameLabel: 'השם שלך',
      namePlaceholder: 'ישראל ישראלי',
      messageLabel: 'הודעה',
      messagePlaceholder: 'מה תרצו לשתף?',
      nameRequired: 'נא להזין שם.',
      messageRequired: 'נא להזין הודעה.',
      genericError: 'נא לתקן את השגיאות למעלה.',
      sendFailed: 'השליחה נכשלה. נסו שוב.',
      sending: 'שולח…',
      send: 'שליחה',
      cancel: 'ביטול',
      close: 'סגירה',
      thankYou: 'תודה רבה!',
      successMessage: 'ההודעה נשלחה בהצלחה. אחזור אליכם בקרוב.',
      successMessageAlt: 'ההודעה נשלחה בהצלחה!',
      gotIt: 'הבנתי',
    },
  },
};

export default content;
